/**
 * LuxireStorage — Shopify App Proxy client for customer profiles & wishlists.
 *
 * Server-only: all data stored in Shopify metafields via App Proxy.
 * No localStorage. In-memory cache for the page session only.
 * Guests are redirected to login when attempting profile/wishlist actions.
 *
 * Usage:
 *   const profiles = await LuxireStorage.getMeasurementProfiles();
 *   await LuxireStorage.saveMeasurementProfile({ name: 'My Pants', ... });
 */

const LuxireStorage = (() => {

  const PROXY_BASE = '/apps/luxire-api';

  // In-memory cache (lives only for the current page session)
  let _cache = {
    measurements: null,
    styles: null,
    wishlist: null,
  };

  // True if customer is logged in (set by Liquid in the page)
  function isLoggedIn() {
    return !!window.__luxireCustomerId;
  }

  // Redirect guests to login, returning them to the current page after
  function requireLogin() {
    if (isLoggedIn()) return true;
    window.location = '/account/login?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
    return false;
  }

  // -------------------------------------------------------------------------
  // API helper
  // -------------------------------------------------------------------------

  async function api(method, path, body) {
    var options = {
      method: method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (body) options.body = JSON.stringify(body);

    var response = await fetch(PROXY_BASE + path, options);
    var data = await response.json();

    if (!response.ok) {
      console.error('LuxireStorage API error: ' + method + ' ' + path, data);
      throw new Error(data.error || 'api_error');
    }

    return data;
  }

  // -------------------------------------------------------------------------
  // Unwrap helper — server may return { version, profiles: [...] } or [...]
  // -------------------------------------------------------------------------

  function unwrapProfiles(data) {
    if (Array.isArray(data)) return data;
    if (data && data.profiles && Array.isArray(data.profiles)) return data.profiles;
    return [];
  }

  // -------------------------------------------------------------------------
  // MEASUREMENT PROFILES
  // -------------------------------------------------------------------------

  async function getMeasurementProfiles() {
    if (!requireLogin()) return [];

    if (_cache.measurements) return _cache.measurements;

    try {
      var result = await api('GET', '/profiles/measurements');
      _cache.measurements = unwrapProfiles(result.profiles);
      return _cache.measurements;
    } catch (e) {
      console.warn('LuxireStorage: failed to fetch measurement profiles', e);
      return [];
    }
  }

  async function saveMeasurementProfile(profile) {
    if (!requireLogin()) return null;

    var result = profile.id
      ? await api('PUT', '/profiles/measurements/' + profile.id, profile)
      : await api('POST', '/profiles/measurements', profile);

    // Refresh cache
    var all = await api('GET', '/profiles/measurements');
    _cache.measurements = unwrapProfiles(all.profiles);

    return result.profile;
  }

  async function deleteMeasurementProfile(id) {
    if (!requireLogin()) return;

    await api('DELETE', '/profiles/measurements/' + id);

    var all = await api('GET', '/profiles/measurements');
    _cache.measurements = unwrapProfiles(all.profiles);
  }

  // Sync read: returns in-memory cache (may be null if not fetched yet)
  function getMeasurementProfilesSync() {
    return _cache.measurements || [];
  }

  // Force-fetch from server (bypass cache)
  async function fetchMeasurementProfiles() {
    if (!isLoggedIn()) return [];
    try {
      var result = await api('GET', '/profiles/measurements');
      _cache.measurements = unwrapProfiles(result.profiles);
      return _cache.measurements;
    } catch (e) {
      return _cache.measurements || [];
    }
  }

  // -------------------------------------------------------------------------
  // STYLE PROFILES
  // -------------------------------------------------------------------------

  async function getStyleProfiles() {
    if (!requireLogin()) return [];

    if (_cache.styles) return _cache.styles;

    try {
      var result = await api('GET', '/profiles/styles');
      _cache.styles = unwrapProfiles(result.profiles);
      return _cache.styles;
    } catch (e) {
      console.warn('LuxireStorage: failed to fetch style profiles', e);
      return [];
    }
  }

  async function saveStyleProfile(profile) {
    if (!requireLogin()) return null;

    var result = profile.id
      ? await api('PUT', '/profiles/styles/' + profile.id, profile)
      : await api('POST', '/profiles/styles', profile);

    var all = await api('GET', '/profiles/styles');
    _cache.styles = unwrapProfiles(all.profiles);

    return result.profile;
  }

  async function deleteStyleProfile(id) {
    if (!requireLogin()) return;

    await api('DELETE', '/profiles/styles/' + id);

    var all = await api('GET', '/profiles/styles');
    _cache.styles = unwrapProfiles(all.profiles);
  }

  function getStyleProfilesSync() {
    return _cache.styles || [];
  }

  async function fetchStyleProfiles() {
    if (!isLoggedIn()) return [];
    try {
      var result = await api('GET', '/profiles/styles');
      _cache.styles = unwrapProfiles(result.profiles);
      return _cache.styles;
    } catch (e) {
      return _cache.styles || [];
    }
  }

  // -------------------------------------------------------------------------
  // WISHLIST
  // -------------------------------------------------------------------------

  function normalizeWishlistItem(item) {
    if (typeof item === 'string') {
      return { handle: item, title: '', image: '', price: '', url: '/products/' + item, variantId: null };
    }
    var handle = item.handle || item.product_handle || item.productHandle || '';
    if (!handle) {
      var match = (item.url || item.product_url || '').match(/\/products\/([^?#\/]+)/);
      if (match) handle = match[1];
    }
    return {
      handle: handle,
      title: item.title || item.product_title || '',
      image: item.image || item.featured_image || item.product_image || '',
      price: item.price || item.product_price || '',
      url: item.url || item.product_url || (handle ? '/products/' + handle : ''),
      variantId: item.variantId || item.variant_id || null,
    };
  }

  async function getWishlist() {
    if (!requireLogin()) return [];

    if (_cache.wishlist) return _cache.wishlist;

    try {
      var result = await api('GET', '/wishlist');
      var items = result.items || result.wishlist || result.data || [];
      if (!Array.isArray(items)) items = [];
      _cache.wishlist = items.map(normalizeWishlistItem);
      return _cache.wishlist;
    } catch (e) {
      return [];
    }
  }

  async function addToWishlist(item) {
    if (!requireLogin()) return null;

    var result = await api('POST', '/wishlist', item);

    var all = await api('GET', '/wishlist');
    var items = all.items || all.wishlist || all.data || [];
    if (!Array.isArray(items)) items = [];
    _cache.wishlist = items.map(normalizeWishlistItem);

    return result.item;
  }

  async function removeFromWishlist(handle) {
    if (!requireLogin()) return;

    await api('DELETE', '/wishlist/' + encodeURIComponent(handle));

    var all = await api('GET', '/wishlist');
    var items = all.items || all.wishlist || all.data || [];
    if (!Array.isArray(items)) items = [];
    _cache.wishlist = items.map(normalizeWishlistItem);
  }

  async function refreshWishlist() {
    if (!isLoggedIn()) return [];
    try {
      var result = await api('GET', '/wishlist');
      var items = result.items || result.wishlist || result.data || [];
      if (!Array.isArray(items)) items = [];
      _cache.wishlist = items.map(normalizeWishlistItem);
      return _cache.wishlist;
    } catch (e) {
      return _cache.wishlist || [];
    }
  }

  function isWishlisted(handle) {
    var items = _cache.wishlist || [];
    return items.some(function(w) { return w.handle === handle; });
  }

  function getWishlistSync() {
    return _cache.wishlist || [];
  }

  // -------------------------------------------------------------------------
  // INIT — prefetch profiles for logged-in users
  // -------------------------------------------------------------------------

  async function init() {
    if (!isLoggedIn()) return;

    // Prefetch measurement and style profiles into memory cache
    // so sync reads work on the product page
    try {
      var results = await Promise.allSettled([
        api('GET', '/profiles/measurements'),
        api('GET', '/profiles/styles'),
      ]);
      if (results[0].status === 'fulfilled') {
        _cache.measurements = unwrapProfiles(results[0].value.profiles);
      }
      if (results[1].status === 'fulfilled') {
        _cache.styles = unwrapProfiles(results[1].value.profiles);
      }
    } catch (e) {
      console.warn('LuxireStorage: init prefetch failed', e);
    }
  }

  // -------------------------------------------------------------------------
  // PUBLIC API
  // -------------------------------------------------------------------------

  return {
    init: init,
    isLoggedIn: isLoggedIn,
    requireLogin: requireLogin,

    // Measurement profiles
    getMeasurementProfiles: getMeasurementProfiles,
    saveMeasurementProfile: saveMeasurementProfile,
    deleteMeasurementProfile: deleteMeasurementProfile,
    getMeasurementProfilesSync: getMeasurementProfilesSync,
    fetchMeasurementProfiles: fetchMeasurementProfiles,

    // Style profiles
    getStyleProfiles: getStyleProfiles,
    saveStyleProfile: saveStyleProfile,
    deleteStyleProfile: deleteStyleProfile,
    getStyleProfilesSync: getStyleProfilesSync,
    fetchStyleProfiles: fetchStyleProfiles,

    // Wishlist
    getWishlist: getWishlist,
    addToWishlist: addToWishlist,
    removeFromWishlist: removeFromWishlist,
    refreshWishlist: refreshWishlist,
    isWishlisted: isWishlisted,
    getWishlistSync: getWishlistSync,

    // Raw API access (for support hub and other modules)
    api: api,
  };

})();
